import { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import { Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/common/PageHeader'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { authService } from '@/services/authService'
import { useAuth } from '@/hooks/useAuth'
import { TotpStatus } from '@/types/auth'
import { tokens } from '@/theme'
import SegurancaPage from './SegurancaPage'
import PerfilPage from '@/pages/perfil/PerfilPage'

/**
 * Auto-visão do consultor em /usuarios/meu-acesso (Figma "Usuários", visão do próprio
 * usuário — Onda 8 / A16). Só dados reais: identidade da sessão (store), estado do MFA em
 * `GET /v1/auth/totp/status` e a sessão atual (cookie HttpOnly deste navegador).
 *
 * "Último acesso": o BFF não expõe esse dado (não há endpoint nem campo em `/v1/auth/me`);
 * a tela mostra "—" com a explicação, em vez de inventar uma data. Lacuna relatada.
 *
 * `?aba=seguranca` abre a gestão do MFA (SegurancaPage) e `?aba=perfil` a conta e a troca
 * de senha (PerfilPage) — o `?aba=perfil` da rota antiga (/perfil) continua valendo.
 */
export type AbaMeuAcesso = 'acesso' | 'seguranca' | 'perfil'

export function lerAbaMeuAcesso(valor: string | null): AbaMeuAcesso {
  return valor === 'seguranca' || valor === 'perfil' ? valor : 'acesso'
}

const Rotulo: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography variant="caption" sx={{ color: tokens.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
    {children}
  </Typography>
)

const Bloco: FC<{ icone: ReactNode; titulo: string; children: ReactNode; acao?: ReactNode }> = ({ icone, titulo, children, acao }) => (
  <Card variant="outlined" sx={{ borderColor: tokens.divider, height: '100%' }}>
    <CardContent>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ color: tokens.brandPrimary, display: 'flex' }}>{icone}</Box>
        <Typography fontWeight={700} sx={{ flex: 1 }}>
          {titulo}
        </Typography>
        {acao}
      </Stack>
      {children}
    </CardContent>
  </Card>
)

const MeuAcessoPage: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const aba = lerAbaMeuAcesso(searchParams.get('aba'))
  const { usuario } = useAuth()

  const [mfa, setMfa] = useState<TotpStatus | null>(null)
  const [carregandoMfa, setCarregandoMfa] = useState(true)
  const [erroMfa, setErroMfa] = useState(false)

  const carregarMfa = useCallback(async () => {
    setCarregandoMfa(true)
    setErroMfa(false)
    try {
      setMfa(await authService.totpStatus())
    } catch {
      setErroMfa(true)
    } finally {
      setCarregandoMfa(false)
    }
  }, [])

  useEffect(() => {
    if (aba === 'acesso') carregarMfa()
  }, [aba, carregarMfa])

  const trocarAba = (nova: AbaMeuAcesso) => {
    const next = new URLSearchParams(searchParams)
    if (nova === 'acesso') next.delete('aba')
    else next.set('aba', nova)
    setSearchParams(next, { replace: true })
  }

  const permissoes = usuario?.permissoes ?? []

  return (
    <Box>
      <PageHeader title="Usuários" subtitle="Seu usuário, verificação em duas etapas e sessão atual" />

      <Tabs value={aba} onChange={(_, v: AbaMeuAcesso) => trocarAba(v)} aria-label="Seções do meu acesso" sx={{ mb: 3, borderBottom: `1px solid ${tokens.divider}` }}>
        <Tab value="acesso" label="Visão geral" />
        <Tab value="seguranca" label="Segurança (MFA)" />
        <Tab value="perfil" label="Conta e senha" />
      </Tabs>

      {aba === 'seguranca' && <SegurancaPage />}
      {aba === 'perfil' && <PerfilPage />}

      {aba === 'acesso' && (
        <Box
          data-testid="auto-visao"
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}
        >
          {/* ─── Identidade ─── */}
          <Bloco icone={<PersonOutlineIcon />} titulo="Seu usuário">
            <Stack spacing={1.25}>
              <Box>
                <Rotulo>Nome</Rotulo>
                <Typography>{usuario?.nome ?? '—'}</Typography>
              </Box>
              <Box>
                <Rotulo>E-mail</Rotulo>
                <Typography>{usuario?.email ?? '—'}</Typography>
              </Box>
              <Box>
                <Rotulo>Perfis</Rotulo>
                <Stack direction="row" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                  {permissoes.length > 0 ? (
                    permissoes.map((p) => <Chip key={p} label={p} size="small" variant="outlined" color="primary" />)
                  ) : (
                    <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                      —
                    </Typography>
                  )}
                </Stack>
              </Box>
              {usuario?.tipoCanal && (
                <Box>
                  <Rotulo>Canal</Rotulo>
                  <Typography>{usuario.tipoCanal}</Typography>
                </Box>
              )}
            </Stack>
          </Bloco>

          {/* ─── MFA (estado real do TOTP) ─── */}
          <Bloco
            icone={<ShieldOutlinedIcon />}
            titulo="Verificação em duas etapas (MFA)"
            acao={
              <Button size="small" variant="outlined" onClick={() => trocarAba('seguranca')}>
                Gerenciar MFA
              </Button>
            }
          >
            {carregandoMfa ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                  Consultando o estado do MFA…
                </Typography>
              </Stack>
            ) : erroMfa || !mfa ? (
              <EstadoDeLista
                estado="erro"
                mensagem="Não foi possível consultar o MFA"
                acao={{ label: 'Tentar novamente', onClick: carregarMfa }}
                minHeight={120}
              />
            ) : (
              <Stack spacing={1.25}>
                <Chip
                  data-testid="mfa-status"
                  size="small"
                  label={mfa.enabled ? 'MFA ativo' : 'MFA inativo'}
                  color={mfa.enabled ? 'success' : 'warning'}
                  sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
                />
                {mfa.enabled ? (
                  <Box>
                    <Rotulo>Códigos de recuperação restantes</Rotulo>
                    <Typography fontWeight={700} sx={{ color: mfa.recoveryCodesRemaining <= 2 ? tokens.statusDanger : 'text.primary' }}>
                      {mfa.recoveryCodesRemaining}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                    O autenticador será configurado no seu próximo login.
                  </Typography>
                )}
              </Stack>
            )}
          </Bloco>

          {/* ─── Sessão atual ─── */}
          <Bloco
            icone={<DevicesOutlinedIcon />}
            titulo="Sessão atual"
            acao={
              <Button size="small" variant="outlined" onClick={() => trocarAba('perfil')}>
                Alterar senha
              </Button>
            }
          >
            <Stack spacing={1.25}>
              <Chip size="small" label="Sessão ativa neste navegador" color="success" variant="outlined" sx={{ alignSelf: 'flex-start' }} />
              <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                Autenticada por cookie seguro (HttpOnly); é encerrada ao sair ou quando o servidor a expira.
                Outras sessões suas não são listadas pelo portal.
              </Typography>
            </Stack>
          </Bloco>

          {/* ─── Último acesso (sem dado no BFF) ─── */}
          <Bloco icone={<HistoryOutlinedIcon />} titulo="Último acesso">
            <Stack spacing={0.5}>
              <Tooltip title="O portal ainda não registra a data do último acesso do usuário.">
                <Typography variant="h6" fontWeight={700} data-testid="ultimo-acesso">
                  —
                </Typography>
              </Tooltip>
              <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                Ainda não registrado pelo portal.
              </Typography>
            </Stack>
          </Bloco>

          <Divider sx={{ gridColumn: '1 / -1', borderColor: tokens.divider }} />
          <Typography variant="caption" sx={{ gridColumn: '1 / -1', color: tokens.textSecondary }}>
            Precisa mudar nome, e-mail ou telefone? Isso fica em Meus Dados.
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default MeuAcessoPage
